import { memo, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useWindowSize } from 'root/src/client/v2/common/hooks/useWindowSize';
import { columnsForWidth } from 'root/src/client/v2/_pages/drop/redesign/dropGrid.helpers';
import { buildAssetUrl, assetSize, resolveThumbnailPlaceholders } from 'root/src/client/v2/common/helpers/assetUrl';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const INITIAL_ROWS = 2;
const ROWS_PER_STEP = 2;
const CreatorCard = ({ item }) => (<a href={item.url} target="_blank" rel="noreferrer" className="group block overflow-hidden rounded-2xl border border-solid border-white/[0.06] bg-card no-underline transition-colors hover:bg-card/80">
		<div className="relative aspect-video overflow-hidden">
			<Img src={buildAssetUrl(resolveThumbnailPlaceholders(item.thumbnail), { w: assetSize('mobile'), q: 80, fmt: 'webp' })} alt={item.creator.displayName} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"/>
			{item.isStream && (<span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-md border border-solid border-neon-green/40 bg-black/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neon-green shadow-sm backdrop-blur-md">
					<span className="h-1.5 w-1.5 rounded-full bg-neon-green"/>
					Live
				</span>)}
		</div>
		<div className="flex items-center gap-3 p-4">
			<Img src={buildAssetUrl(item.creator.avatar, { w: assetSize('thumbnail'), q: 80, fmt: 'webp' })} alt={item.creator.displayName} className="h-9 w-9 shrink-0 rounded-full border border-solid border-white/10 object-cover"/>
			<div className="min-w-0">
				<p className="truncate text-sm font-semibold text-foreground">{item.creator.displayName}</p>
				<p className="truncate text-xs text-muted-foreground">
					{item.creator.followerCount.toLocaleString()} followers &middot; {item.creator.deliveryCount} dares
				</p>
			</div>
		</div>
	</a>);
const CreatorsSkeleton = () => (<div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
		{Array.from({ length: 3 }).map((_, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={index} className="overflow-hidden rounded-2xl border border-solid border-white/[0.06] bg-card">
				<Skeleton className="aspect-video w-full rounded-none"/>
				<div className="flex items-center gap-3 p-4">
					<Skeleton className="h-9 w-9 rounded-full"/>
					<div className="space-y-2"><Skeleton className="h-3.5 w-24"/><Skeleton className="h-3 w-32"/></div>
				</div>
			</div>))}
	</div>);
export const DropCreatorsSection = memo(({ mnemonicId }) => {
    var _a, _b, _c, _d, _e;
    const query = trpc.drops.getDropMedia.useInfiniteQuery({ mnemonicId }, {
        enabled: Boolean(mnemonicId),
        staleTime: 1000 * 60,
        initialCursor: 1,
        getNextPageParam: lastPage => lastPage.nextCursor,
    });
    const media = useMemo(() => { var _a, _b; return ((_b = (_a = query.data) === null || _a === void 0 ? void 0 : _a.pages.flatMap(page => { var _a; return (_a = page.items) !== null && _a !== void 0 ? _a : []; })) !== null && _b !== void 0 ? _b : []); }, [(_a = query.data) === null || _a === void 0 ? void 0 : _a.pages]);
    const total = (_e = (_d = (_c = (_b = query.data) === null || _b === void 0 ? void 0 : _b.pages) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.total) !== null && _e !== void 0 ? _e : media.length;
    const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
    const [width] = useWindowSize();
    const columns = columnsForWidth(width);
    const [visibleRows, setVisibleRows] = useState(INITIAL_ROWS);
    const visibleCount = visibleRows * columns;
    const visibleMedia = useMemo(() => media.slice(0, visibleCount), [media, visibleCount]);
    const canShowMore = media.length > visibleCount || Boolean(hasNextPage);
    // Button-driven loading (no infinite scroll): fetch the next page only when the
    // currently requested rows need more items than are already loaded.
    useEffect(() => {
        if (visibleCount > media.length && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [visibleCount, media.length, hasNextPage, isFetchingNextPage, fetchNextPage]);
    const onShowMore = () => setVisibleRows(rows => rows + ROWS_PER_STEP);
    return (<section id="creators" className="scroll-mt-40 px-4 py-12 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">Creators</h2>
				<p className="mt-1 text-sm text-muted-foreground">
					{query.isLoading ? 'Loading the race...' : total ? `${total} in the race for rewards` : 'No creators have joined the race yet.'}
				</p>
				{query.isLoading && <CreatorsSkeleton />}
				{!query.isLoading && media.length > 0 && (<>
						<motion.div layout className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							<AnimatePresence mode="popLayout">
								{visibleMedia.map(item => (<motion.div key={`${item.platform}-${item.platformId}-${item.url}`} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }} className="min-w-0">
										<CreatorCard item={item}/>
									</motion.div>))}
							</AnimatePresence>
						</motion.div>
						{canShowMore && (<div className="mt-8 flex justify-center">
								<Button variant="outline" onClick={onShowMore} loading={isFetchingNextPage} className="rounded-full px-6">
									Show more
									<ExpandMoreIcon />
								</Button>
							</div>)}
					</>)}
			</div>
		</section>);
});
